import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { PrintQrModal } from "@/components/PrintQrModal";
import {
  adminStationsApi,
  adminTagsApi,
  adminUsersApi,
  devicesApi,
  flowsApi,
  ordersApi,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  TagKind as TAG_KINDS,
  TagTargetType as TAG_TARGETS,
  type TagCreate,
  type TagKind,
  type TagRead,
  type TagTargetType,
} from "@/lib/types";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

export default function TagsList() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<TagKind | "">("");
  const [targetType, setTargetType] = useState<TagTargetType | "">("");
  const [active, setActive] = useState<"yes" | "no" | "all">("yes");
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);

  const query = useQuery({
    queryKey: ["admin", "tags", "all", { kind, targetType, active, q }],
    queryFn: () =>
      adminTagsApi.list({
        kind: (kind || undefined) as TagKind | undefined,
        target_type: (targetType || undefined) as TagTargetType | undefined,
        is_active: active === "all" ? undefined : active === "yes",
        q: q.trim() || undefined,
        page_size: 200,
      }),
  });

  return (
    <RequirePermission codes="tag.manage">
      <PageHeader
        eyebrow="admin · tags"
        title={t("tagsList.title")}
        description={t("tagsList.description")}
        actions={
          <button className="btn btn-primary" onClick={() => setOpenCreate(true)}>
            {t("tagsList.bindTag")}
          </button>
        }
      />

      <div className="card p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <Field label={t("tagsList.kindFilter")}>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as TagKind | "")}
            className={`${INPUT_CLS} font-mono text-xs`}
          >
            <option value="">{t("tagsList.allFilter")}</option>
            {TAG_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("tagsList.targetFilter")}>
          <select
            value={targetType}
            onChange={(e) => setTargetType(e.target.value as TagTargetType | "")}
            className={`${INPUT_CLS} font-mono text-xs`}
          >
            <option value="">{t("tagsList.allFilter")}</option>
            {TAG_TARGETS.map((tt) => (
              <option key={tt} value={tt}>
                {labelForTarget(tt, t)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("tagsList.activeFilter")}>
          <select
            value={active}
            onChange={(e) => setActive(e.target.value as "yes" | "no" | "all")}
            className={`${INPUT_CLS} font-mono text-xs`}
          >
            <option value="yes">{t("tagsList.onlyActive")}</option>
            <option value="no">{t("tagsList.onlyUnbound")}</option>
            <option value="all">{t("tagsList.allFilter")}</option>
          </select>
        </Field>
        <Field label={t("tagsList.searchLabel")}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={INPUT_CLS}
            placeholder="NFC_OP_001"
          />
        </Field>
      </div>

      {query.isLoading ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState error={query.error} />
      ) : query.data!.items.length === 0 ? (
        <EmptyState
          title={t("tagsList.empty")}
          hint={t("tagsList.emptyHint")}
        />
      ) : (
        <TagsTable items={query.data!.items} />
      )}

      {openCreate && <CreateTagDialog onClose={() => setOpenCreate(false)} />}
    </RequirePermission>
  );
}

function TagsTable({ items }: { items: TagRead[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const unbind = useMutation({
    mutationFn: (id: string) => adminTagsApi.unbind(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "tags"] }),
  });
  // Печать QR прямо из списка меток (тот же диалог, что на /admin/stations —
  // с «сохранить в PDF» / «скачать»). Режимы:
  //   station — печать QR станции (station-qr);
  //   reprint — перепечать ТОТ ЖЕ бейдж оператора (operator-badge, тот же токен);
  //   new     — выдать НОВЫЙ бейдж оператора (новый токен).
  const [print, setPrint] = useState<
    { tag: TagRead; mode: "station" | "reprint" | "new" } | null
  >(null);

  return (
    <>
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-alt">
          <tr className="text-left text-text-muted font-mono text-xs uppercase">
            <th className="px-4 py-3">{t("tagsList.colKind")}</th>
            <th className="px-4 py-3">{t("tagsList.colValue")}</th>
            <th className="px-4 py-3">{t("tagsList.colTarget")}</th>
            <th className="px-4 py-3">{t("tagsList.colTargetId")}</th>
            <th className="px-4 py-3">{t("tagsList.colCreated")}</th>
            <th className="px-4 py-3">{t("tagsList.colLastScan")}</th>
            <th className="px-4 py-3">{t("tagsList.colStatus")}</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((tag) => (
            <tr key={tag.id} className="border-t border-border hover:bg-surface-hover">
              <td className="px-4 py-3">
                <span className="pill pill-accent font-mono text-xs">{tag.kind}</span>
              </td>
              <td className="px-4 py-3 font-mono break-all min-w-[150px]">{tag.value}</td>
              <td className="px-4 py-3 text-xs">
                <div className="text-text-secondary">{labelForTarget(tag.target_type, t)}</div>
                {tag.target_label && (
                  <div className="text-text-primary font-medium mt-0.5">{tag.target_label}</div>
                )}
              </td>
              <td
                className="px-4 py-3 font-mono text-xs text-text-muted max-w-[110px] truncate"
                title={tag.target_id}
              >
                {tag.target_id}
              </td>
              <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">
                {formatDateTime(tag.created_at)}
              </td>
              <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">
                {tag.last_seen_at ? formatDateTime(tag.last_seen_at) : "—"}
              </td>
              <td className="px-4 py-3">
                <span className={`pill ${tag.is_active ? "pill-ok" : ""}`}>
                  {tag.is_active ? t("tagsList.enabled") : t("tagsList.unbound")}
                </span>
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                {tag.target_type === "station" && (
                  <button
                    type="button"
                    className="btn text-xs mr-2"
                    onClick={() => setPrint({ tag, mode: "station" })}
                    title={t("tagsList.printQrHint")}
                  >
                    🖨 QR
                  </button>
                )}
                {tag.target_type === "operator" && (
                  <>
                    <button
                      type="button"
                      className="btn text-xs mr-2"
                      onClick={() => setPrint({ tag, mode: "reprint" })}
                      title={t("tagsList.reprintHint")}
                    >
                      🖨 QR
                    </button>
                    <button
                      type="button"
                      className="btn text-xs mr-2"
                      onClick={() => setPrint({ tag, mode: "new" })}
                      title={t("tagsList.newBadgeHint")}
                    >
                      🆕
                    </button>
                  </>
                )}
                {tag.is_active && (
                  <button
                    type="button"
                    className="btn btn-danger text-xs"
                    onClick={() => {
                      if (confirm(t("tagsList.confirmUnbind", { kind: tag.kind, value: tag.value }))) unbind.mutate(tag.id);
                    }}
                    disabled={unbind.isPending}
                  >
                    {t("tagsList.unbind")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {print && (
      <PrintQrModal
        templateKind={print.mode === "station" ? "station-qr" : "operator-badge"}
        subjectLabel={print.tag.target_label ?? print.tag.value}
        onClose={() => setPrint(null)}
        onPrint={async (target) => {
          if (print.mode === "station")
            return adminStationsApi.printQr(Number(print.tag.target_id), target);
          if (print.mode === "reprint")
            return adminTagsApi.printTag(print.tag.id, target);
          // mode "new": выпускаем свежий бейдж оператора (новый токен).
          const r = await adminUsersApi.issueQrBadge(print.tag.target_id, target);
          return { job_id: r.print_job_id, status: "queued" };
        }}
      />
    )}
    </>
  );
}

function CreateTagDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [data, setData] = useState<TagCreate>({
    kind: "NFC",
    value: "",
    target_type: "operator",
    target_id: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => adminTagsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tags"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  function patch<K extends keyof TagCreate>(k: K, v: TagCreate[K]) {
    setData((d) => ({ ...d, [k]: v }));
  }

  return (
    <div className="fixed inset-0 bg-page/70 backdrop-blur z-20 flex items-center justify-center p-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          m.mutate();
        }}
        className="card p-6 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between gap-4">
          <h2 className="text-xl">{t("tagsList.modalTitle")}</h2>
          <button type="button" className="btn text-xs" onClick={onClose}>
            ×
          </button>
        </header>

        <Field label={t("tagsList.kindFilter")} required>
          <select
            className={`${INPUT_CLS} font-mono`}
            value={data.kind}
            onChange={(e) => patch("kind", e.target.value as TagKind)}
          >
            {TAG_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("tagsList.valueLabel")} required>
          <input
            className={`${INPUT_CLS} font-mono`}
            value={data.value}
            onChange={(e) => patch("value", e.target.value)}
            placeholder="NFC_OP_001"
            autoFocus
          />
        </Field>
        <Field label={t("tagsList.bindToLabel")} required>
          <select
            className={`${INPUT_CLS} font-mono`}
            value={data.target_type}
            onChange={(e) =>
              // При смене типа цели сбрасываем выбранный target_id —
              // он был валиден только для предыдущего типа.
              setData((d) => ({
                ...d,
                target_type: e.target.value as TagTargetType,
                target_id: "",
              }))
            }
          >
            {TAG_TARGETS.map((tt) => (
              <option key={tt} value={tt}>
                {labelForTarget(tt, t)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("tagsList.entityLabel")} required>
          <TargetPicker
            type={data.target_type}
            selectedId={data.target_id}
            onSelect={(id) => patch("target_id", id)}
          />
        </Field>

        <p className="text-xs text-text-muted">
          {t("tagsList.uniqueHint")}
        </p>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button type="button" className="btn" onClick={onClose} disabled={m.isPending}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={m.isPending || !data.value.trim() || !data.target_id.trim()}
          >
            {m.isPending ? t("tagsList.binding") : t("tagsList.bind")}
          </button>
        </div>
      </form>
    </div>
  );
}

// Combobox с поиском.  Закрытый — input с выбранным значением и
// шевроном.  По клику открывается dropdown: поле поиска + список
// до 30 первых результатов.  Источник зависит от target_type.
function TargetPicker({
  type,
  selectedId,
  onSelect,
}: {
  type: TagTargetType;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Сбрасываем поиск при смене типа цели (иначе остаётся «admin» из
  // прошлого выбора и список пустой).
  useEffect(() => {
    setQ("");
    setOpen(false);
  }, [type]);

  // Click-outside закрытие.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const users = useQuery({
    queryKey: ["pick", "operators", q],
    queryFn: () =>
      adminUsersApi.list({
        q: q.trim() || undefined,
        is_active: true,
        page_size: 30,
      }),
    enabled: type === "operator",
  });
  const stations = useQuery({
    queryKey: ["pick", "stations", q],
    queryFn: () =>
      adminStationsApi.list({
        q: q.trim() || undefined,
        is_active: true,
        page_size: 30,
      }),
    enabled: type === "station",
  });
  const devices = useQuery({
    queryKey: ["pick", "devices", q],
    queryFn: () => devicesApi.list({ q: q.trim() || undefined, page_size: 30 }),
    enabled: type === "device",
  });
  const orders = useQuery({
    queryKey: ["pick", "orders", q],
    queryFn: () =>
      ordersApi.list({ code: q.trim() || undefined, page_size: 30 }),
    enabled: type === "order",
  });
  const flows = useQuery({
    queryKey: ["pick", "flows", q],
    queryFn: () =>
      flowsApi.list({ code: q.trim() || undefined, page_size: 30 }),
    enabled: type === "flow_item",
  });

  type Row = { id: string; primary: string; secondary?: string };
  const rows: Row[] = (() => {
    switch (type) {
      case "operator":
        return (users.data?.items ?? []).map((u) => ({
          id: u.id,
          primary: u.name,
          secondary: `${u.role}${u.login ? " · " + u.login : ""}`,
        }));
      case "station":
        return (stations.data?.items ?? []).map((s) => ({
          id: String(s.id),
          primary: s.code,
          secondary: s.name,
        }));
      case "device":
        return (devices.data?.items ?? []).map((d) => ({
          id: d.id,
          primary: d.code,
          secondary: d.type,
        }));
      case "order":
        return (orders.data?.items ?? []).map((o) => ({
          id: o.id,
          primary: o.code,
          secondary: `${t("tagsList.stagePrefix")} ${o.stage_id} · ${o.status}`,
        }));
      case "flow_item":
        return (flows.data?.items ?? []).map((f) => ({
          id: f.id,
          primary: f.code,
          secondary: `${f.flow_type} · ${f.status}`,
        }));
    }
  })();

  const selectedRow = rows.find((r) => r.id === selectedId);

  return (
    <div className="relative" ref={wrapRef}>
      {/* Закрытый display.  Клик — открыть dropdown. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${INPUT_CLS} flex items-center justify-between gap-2 cursor-pointer text-left`}
      >
        {selectedRow ? (
          <span className="min-w-0 truncate">
            <span className="font-medium">{selectedRow.primary}</span>
            {selectedRow.secondary && (
              <span className="text-text-muted text-xs ml-2">
                {selectedRow.secondary}
              </span>
            )}
          </span>
        ) : (
          <span className="text-text-muted">{t("tagsList.pickEntity")}</span>
        )}
        <span className="text-text-muted text-xs shrink-0">
          {selectedId ? (
            <button
              type="button"
              className="text-text-muted hover:text-danger px-1"
              aria-label={t("common.reset")}
              onClick={(e) => {
                e.stopPropagation();
                onSelect("");
              }}
            >
              ×
            </button>
          ) : (
            <>▾</>
          )}
        </span>
      </button>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-md border border-border bg-page shadow-lg overflow-hidden">
          <input
            autoFocus
            className="w-full px-3 py-2 bg-surface border-b border-border outline-none"
            placeholder={t("tagsList.searchByNameCode")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <ul className="max-h-64 overflow-y-auto divide-y divide-border">
            {rows.length === 0 ? (
              <li className="px-3 py-3 text-text-muted text-sm text-center">
                {t("tagsList.noResults")}
              </li>
            ) : (
              rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-hover ${
                      r.id === selectedId ? "bg-surface" : ""
                    }`}
                    onClick={() => {
                      onSelect(r.id);
                      setOpen(false);
                    }}
                  >
                    <p>{r.primary}</p>
                    {r.secondary && (
                      <p className="text-text-muted text-xs mt-0.5">
                        {r.secondary}
                      </p>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function labelForTarget(target: TagTargetType, t: TFunction): string {
  return t(`tagsList.targets.${target}`);
}

